import { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';

import { trans } from '@spotahome/soyuz/client';
import Metrics from '@spotahome/soyuz-tracking';
import { Mediator } from '@spotahome/soyuz-mediator';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { Button, Checkbox, useModalHandle } from '@spotahome/ui-library';
import {
  getItem,
  removeItem,
  setItem
} from '@spotahome/ui-library/src/utils/localStorage';

import {
  OPEN_BOOKNOW_DATEPICKER_DESKTOP,
  OPEN_BOOKNOW_DATEPICKER_MOBILE,
  NAVIGATE_CHECKOUT_FUNNEL,
  COMMON_LOGIN_MODAL
} from '@spotahome/marketplace-common/src/channels';
import suggestPrice from '@spotahome/marketplace-common/src/graphql/mutations/suggest-price';
import getPriceSuggestions from '@spotahome/marketplace-common/src/graphql/queries/suggest-price';
import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';

import SuggestPriceModal from './SuggestPriceModal';

import './SuggestPrice.scss';

const OPEN_MODAL_EVENTS = {
  open: {
    eventCategory: 'signup/login modal',
    eventAction: 'Make-offer',
    eventLabel: 'open'
  },
  close: {
    eventCategory: 'signup/login modal',
    eventAction: 'Make-offer',
    eventLabel: 'close'
  }
};

const WAITING_FOR_LOGIN = 'waiting';
const SUGGESTION_DONE = 'done';
const NOT_DONE = 'not_done';

const SuggestPrice = ({
  listingId,
  isLogged,
  currencyIsoCode,
  offerPrice,
  deviceType,
  hasDates
}) => {
  const [suggestPriceButtonRef, inView] = useInView({
    triggerOnce: true,
    rootMargin: '100px'
  });
  const [offerStatus, setOfferStatus] = useState(SUGGESTION_DONE);
  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();

  const makeOfferKey = `makeAnOffer-${listingId}`;

  const handleMakeOfferOpenModal = () => {
    if (!isLogged) {
      setItem(makeOfferKey, WAITING_FOR_LOGIN);
      Mediator.publish(COMMON_LOGIN_MODAL, {
        initialAction: 'signup',
        events: OPEN_MODAL_EVENTS,
        renderTitle: action => trans(`${action}.modal.title`),
        renderSubtitle: action => trans(`${action}.modal.subtitle`)
      });
    } else {
      SoyuzAnalytics.sendGA4Event('open', { section: 'suggest-price-modal' });
      removeItem(makeOfferKey);
      handleModalOpen();
    }
  };

  const handleOfferIsMade = async offer => {
    await suggestPrice(getGqlClient())({ listingId, suggestedPrice: offer });
    Metrics.actions.sendActionEvent(Metrics.actions.SUGGEST_PRICE, {
      listingId,
      suggestion: offer,
      currency: currencyIsoCode
    });
    SoyuzAnalytics.sendGA4Event('price-suggested', {
      value: offer,
      item_id: listingId
    });
    setOfferStatus(SUGGESTION_DONE);
    setItem(makeOfferKey, SUGGESTION_DONE);
  };

  const handleEqualOrHigherPriceSuggestion = () => {
    if (!hasDates) {
      const openCalendarDeviceEvent =
        deviceType === 'smartphone' || deviceType === 'tablet'
          ? OPEN_BOOKNOW_DATEPICKER_MOBILE
          : OPEN_BOOKNOW_DATEPICKER_DESKTOP;

      Mediator.publish(openCalendarDeviceEvent);
    } else {
      Mediator.publish(NAVIGATE_CHECKOUT_FUNNEL);
    }
  };

  const requestPriceSuggestions = async () => {
    try {
      const { data } = await getPriceSuggestions(getGqlClient())([listingId]);
      return data.priceSuggestions;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const openModalAfterLogin = () => {
    handleMakeOfferOpenModal();
    removeItem(makeOfferKey);
    setOfferStatus(NOT_DONE);
  };

  const getVariantOfferStatus = async storedOfferStatus => {
    const priceSuggestions = await requestPriceSuggestions();
    if (storedOfferStatus === WAITING_FOR_LOGIN && !priceSuggestions?.length) {
      openModalAfterLogin();
    } else if (priceSuggestions?.length) {
      setOfferStatus(SUGGESTION_DONE);
    } else {
      setOfferStatus(NOT_DONE);
    }
  };

  useEffect(() => {
    const storedOfferStatus = getItem(makeOfferKey);
    if (inView || storedOfferStatus === WAITING_FOR_LOGIN) {
      getVariantOfferStatus(storedOfferStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <Fragment>
      <div className="suggest-price" ref={suggestPriceButtonRef}>
        <Button
          onClick={handleMakeOfferOpenModal}
          alt="true"
          dataTest="makeAnOffer"
          color="interaction"
          disabled={offerStatus === SUGGESTION_DONE}
          type="button"
        >
          <div className="suggest-price__content">
            <span
              className={`${
                offerStatus !== SUGGESTION_DONE
                  ? 'suggest-price__content--checkbox-no-display'
                  : 'suggest-price__content--checkbox'
              }`}
            >
              <Checkbox id="checkbox" name="checkbox" checked size="small" />
            </span>
            {trans('listing.pricing.suggest.button1')}
          </div>
        </Button>
      </div>
      <SuggestPriceModal
        isOpen={isModalOpen}
        handleOfferIsMade={handleOfferIsMade}
        onModalClose={handleModalClose}
        currencyIsoCode={currencyIsoCode}
        price={offerPrice}
        handleEqualOrHigherPriceSuggestion={handleEqualOrHigherPriceSuggestion}
      />
    </Fragment>
  );
};

SuggestPrice.propTypes = {
  listingId: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  deviceType: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  isLogged: PropTypes.bool.isRequired,
  hasDates: PropTypes.bool.isRequired
};

export default SuggestPrice;
